import React from "react";
import Query from "core/api/QueryComponent";
import { Entity, Field } from "typegraph";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { Icon } from "@blueprintjs/core";
import { RefineTagPanelConfig } from "./RefineTagPanelConfig";
import MenuTreeView, { IMenuNode } from 'common/views/menuTree/MenuTree.view';
import { ID } from 'common/entities/IEntity';
import Loading from 'common/fragments/Loading';
import Label from 'core/components/label/Label';
import { BlockView } from 'common/views/block/BlockView.view';
import { TagView } from 'common/views/tag/TagView.view';
import { CSSProperties } from 'react';

const PARAM_TAGS = "categoryId";
interface AccordionProps {}
interface AccordionState {
	openPanels: number[];
	expanded: { [id: string]: boolean; };
}


class KbCategoryParent {
	@Field()
	public id: any;

	@Field()
	public name: string;
}


@Entity({ many: "kbCategory_many" })
export class Category {
	@Field()
	public id: any;

	@Field()
	public name: string;

	@Field({ entity: KbCategoryParent })
	public parent: KbCategoryParent;
}

@Panel({
	id: "refine-article",
	configClass: RefineTagPanelConfig,
	pageParams: true
})
export class RefineTagPanel extends React.PureComponent<
	IPanelProps<RefineTagPanelConfig, AccordionProps>,
	AccordionState
> {
	constructor(props) {
		super(props);
		this.state = {
			openPanels: [],
			expanded: {}
		};
	}

	private onExpandedStateChange = (node: IMenuNode, isExpanded: boolean) => {
		this.setState({
			expanded: {...this.state.expanded, [node.id]: isExpanded}
		});
	}


	private onSelectTag = (node: IMenuNode<ID>) => {
		const { actions, config, pageParams } = this.props;

		const selected: string[] = pageParams[PARAM_TAGS] && pageParams[PARAM_TAGS].split(',') || [];

		const tagId = node.data;

		let nextValue: any;

		if (!selected) {

			// 1. if nothing selected, always add
			nextValue = tagId;

		} else if (selected.length === 1 && selected[0] === tagId) {

			// 2. if only this one selected, remove the param
			nextValue = "";

		} else {

			// 3. if some already selected, update based on config logic

			nextValue = (selected.includes(tagId) ? selected.filter((id) => id !== tagId) : [...selected, tagId]).join(',');
		}

		// always reset page number when this changes
		actions.pushPageParams({key: PARAM_TAGS, value: nextValue}, {key: 'pageNumber', value: 0});
	}

	componentWillMount(){
		this.props.actions.removePageParams(["entryId", "itemId","subscribed"]);
	}
	componentWillUnmount(){
		this.props.actions.removePageParams([PARAM_TAGS, "pageNumber", "sort"]);
	}

	private isSelected = (tag) => !!this.props.pageParams[PARAM_TAGS] && this.props.pageParams[PARAM_TAGS].split(',').indexOf(tag.id.toString()) > -1;
	
	private renderNestedList = (categories) => {
		return categories.map(category => {
        const id = `tag-${category.id}`;
        const isActive = this.isSelected(category);

			return {
				id,
				label: <Label k={`Tag.${category.id}.name`} v={category.name} />,
				data: category.id,
				children: category.children.length > 0 
					? this.renderNestedList(category.children) 
					: [],
				isExpanded: !!this.state.expanded[id],
				isActive,
				icon: isActive ? <Icon icon="small-tick" /> : undefined,
			};
		});
	};

	private getActiveTagIds = (): string[] => {
		return this.props.pageParams[PARAM_TAGS] && this.props.pageParams[PARAM_TAGS].split(',') || [];
	}
	private onRemoveTag = (id: ID) => {
		this.props.actions.pushPageParam(PARAM_TAGS, this.getActiveTagIds().filter((tid) => tid !== (id).toString()).join(','));
	}

	public render() {
		const {config,pageParams } = this.props;
	
		const activeTagIds: ID[] = pageParams[PARAM_TAGS] ? pageParams[PARAM_TAGS].split(',') : [];
		
		return (
			<React.Fragment>
				<Query.Many
					entity={Category}
					args={{ pageSize: 10000, knowledgeBaseId: config.knowledgeBaseId ||null}}
					key="KnowledgeCategory"
				>
					{args => {
							
							if (args.loading) {
								return <Loading />;
							}

							if (!args.data || args.data.length < 1) {
								return null;
							}

							let tagSets = args.data;
							
							const categoryMap = new Map();
							tagSets.forEach(category => {
								categoryMap.set(category.id, { ...category, children: [] });
							});
							
							tagSets.forEach(category => {
								if (category.parent) {
									const parent = categoryMap.get(category.parent.id);
									if (parent) {
										parent.children.push(categoryMap.get(category.id));
									}
								}
							});

							const nestedList = [];
							categoryMap.forEach(category => {
								if (!category.parent) {
									nestedList.push(category);
								}
							});
							const list = this.renderNestedList(nestedList)
						if (config.sortCategories) {
							// Recursive sort function for n nested layers
							function sortMenuItems(items) {
								if (!Array.isArray(items)) return [];
								// Filter invalid items
								const filtered = items.filter(item => item.label && item.label.props && item.label.props.v);
								// Sort current layer
								filtered.sort((a, b) => a.label.props.v.localeCompare(b.label.props.v));
								// Recursively sort children
								filtered.forEach(item => {
									if (Array.isArray(item.children) && item.children.length > 0) {
											item.children = sortMenuItems(item.children);
										}
									});
								return filtered;
							}
							const sortedList = sortMenuItems(list);
							list.length = 0;
							list.push(...sortedList);
						}
							let childrenList = [...tagSets];
				
						return (
							<div style={{fontSize: config.fontSize, fontWeight: config.fontWeight as CSSProperties['fontWeight'],}}>
								{activeTagIds.length > 0 && (
								   <BlockView viewKey="selected">
										{activeTagIds.map((tag) => {
											let tagInfo = {id: "", name:""}
											if (childrenList && childrenList.length > 0) {
												for (let i = 0; i < childrenList.length; i++) {
													if (childrenList[i].id == tag) {
														tagInfo = childrenList[i]
														break;
													}
												}				
											}
											return (
												<TagView
													viewKey="tag.selected"
													key={tag}
													value={tag}
													onRemove={this.onRemoveTag}
													style={{margin: "0 7px 7px 0"}}
												>
													<Label k={`Tag.${tag}.name`} v={tagInfo && tagInfo.name} />
												</TagView>
											);
										})}
								   </BlockView>
									)
								}

								<MenuTreeView
									viewKey="menu"
									items={list}
									onExpandedStateChange={this.onExpandedStateChange}
									onSelectItem={this.onSelectTag}
								/>
							</div>	
						);
					}}
				</Query.Many>
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/panels/knowledgebase/refineArticle/RefineArticle.panel.tsx